import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  PREMATCH_GAME_TYPE_FOR_SWARM,
  sports as SPORTS_WHAT
} from 'utils/constants/sportsbook/data-fields';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { MenuItem } from 'interfaces/generic';
import { EAdvancedEventsLayouts } from 'interfaces/sportsbook';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/__partials/AdvancedSportsMenu/index.less';
import { Menu } from 'newcomponents/UI/Menu';
import { useGetUserSportOrder } from 'hooks/sportsbook/useGetUserSportOrder';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import { setAdvancedEventsLayoutSports } from 'store/actions/new-sportsbook';
import { getAdvancedEventsLayoutSports } from 'store/selectors/new-sportsbook';

const getWhere = (
  layoutType: EAdvancedEventsLayouts,
  sportType: string | undefined
) => {
  const whereObj = {
    [EAdvancedEventsLayouts.live]: {
      game: { type: 1 },
      sport: {
        type: {
          '@in': getLivePreMatchSportTypes(sportType)
        }
      }
    },
    [EAdvancedEventsLayouts.upcoming]: {
      game: PREMATCH_GAME_TYPE_FOR_SWARM,
      sport: {
        type: {
          '@in': getLivePreMatchSportTypes(sportType)
        }
      }
    },
    [EAdvancedEventsLayouts.popular]: {
      game: { type: { '@in': [0, 1, 2] }, promoted: true },
      sport: {
        type: {
          '@in': getLivePreMatchSportTypes(sportType)
        }
      }
    }
  };

  return whereObj[layoutType];
};

type TAdvancedSportsMenuProps = {
  layoutType: EAdvancedEventsLayouts;
  selectedSport: MenuItem | null | undefined;
  setSelectedSport: (selectedSport: MenuItem | null) => void;
};

export const AdvancedSportsMenu = ({
  layoutType,
  selectedSport,
  setSelectedSport
}: TAdvancedSportsMenuProps) => {
  const sports = useSelector(getAdvancedEventsLayoutSports)?.[layoutType];

  const { mounted } = useMounted();
  const configs = useSportsbookConfigs();
  const dispatch = useDispatch();

  const { orderSports } = useGetUserSportOrder();

  const orderedSports = useMemo(() => {
    if (sports) {
      if (sports.length) {
        return orderSports(sports);
      } else {
        return [];
      }
    }

    return null;
  }, [sports, orderSports]);

  const onSelectItem = useCallback(
    (sport: MenuItem) => {
      if (sport && sport.alias !== selectedSport?.alias) {
        setSelectedSport(sport);
      }
    },
    [selectedSport?.alias]
  );

  useLayoutEffect(() => {
    if (!selectedSport && orderedSports) {
      if (orderedSports.length) {
        setSelectedSport(orderedSports[0] as MenuItem);
      } else {
        setSelectedSport(null);
      }
    }
  }, [selectedSport, orderedSports]);

  useEffect(() => {
    const rid = RidGenerator.gForSubscribe(
      `ADVANCED_SPORTS_${layoutType.toUpperCase()}`
    );

    const onResponse = (data: { sport: Sport }) => {
      if (data && data.sport && mounted.current) {
        dispatch(
          setAdvancedEventsLayoutSports({
            data: sortByOrder(Object.values(data.sport)),
            key: layoutType
          })
        );
      }
    };

    const what = {
      sport: SPORTS_WHAT.sport,
      game: [['id']]
    };

    executeCommand(
      rid,
      what,
      getWhere(layoutType, configs.sportType),
      onResponse,
      true,
      onResponse
    );

    return () => {
      unsubscribeCommand(rid);
    };
  }, []);

  return (
    <div className="advancedSportsMenuWrapper">
      <Menu
        layout="fill"
        items={orderedSports as MenuItem[]}
        selectedItem={selectedSport}
        onSelectItem={onSelectItem}
      />
    </div>
  );
};
